"use client";

import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";

import { garimposDecadeId } from "@/features/garimpos/garimpos-timeline";
import { railFadeEdges } from "@/features/garimpos/rail-fade-edges";
import { useActiveSection } from "@/hooks/use-active-section";
import { useStickyHeaderHeight } from "@/hooks/use-sticky-header-height";
import { cn } from "@/lib/utils";

/**
 * Sticky decade rail: a column beside the spine from `md`, a horizontal strip under the header on
 * phones. Tracks the decade being read and jumps to a decade on click.
 */
export function DecadeRail({ decades }: { decades: readonly number[] }) {
  const t = useTranslations("garimpos");
  const headerHeight = useStickyHeaderHeight();
  const activeId = useActiveSection(decades.map(garimposDecadeId));
  const listRef = useRef<HTMLUListElement>(null);
  // No fade until measured: matches the server-rendered strip, where nothing has overflowed yet.
  const [fadeEdges, setFadeEdges] = useState({ left: false, right: false });

  // Keep the active decade visible inside the horizontal phone strip without moving the page.
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>('[aria-current="location"]');
    if (!list || !active || list.scrollWidth <= list.clientWidth) return;
    const start = active.offsetLeft - list.offsetLeft;
    const end = start + active.offsetWidth;
    if (start >= list.scrollLeft && end <= list.scrollLeft + list.clientWidth) return;
    list.scrollTo({ left: Math.max(0, start - 16) });
  }, [activeId]);

  // Only fade a side that actually hides content, so the first/last chip never looks clipped.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const update = () =>
      setFadeEdges(
        railFadeEdges({
          scrollLeft: list.scrollLeft,
          clientWidth: list.clientWidth,
          scrollWidth: list.scrollWidth,
        }),
      );
    update();
    list.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(list);
    return () => {
      list.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [decades]);

  return (
    <nav
      aria-label={t("railLabel")}
      style={{ top: headerHeight }}
      className="sticky z-20 -mx-4 border-b border-border bg-background px-4 py-2 sm:-mx-8 sm:px-8 md:mx-0 md:self-start md:border-b-0 md:bg-transparent md:px-0 md:pt-4 md:pb-0"
    >
      <ul
        ref={listRef}
        // Phone strip: eight decades share the width evenly and keep their min-content size, so a
        // narrow viewport scrolls horizontally; the mask fades the strip's edges to signal more is
        // reachable, without touching the `h-11` (44px) touch targets. Each side's fade width comes
        // from a CSS var set below, zeroed out while that side has nothing hidden, so the active
        // first/last chip never looks clipped at rest. `md` drops both: the column never scrolls.
        style={
          {
            "--rail-fade-left": fadeEdges.left ? "12px" : "0px",
            "--rail-fade-right": fadeEdges.right ? "12px" : "0px",
          } as CSSProperties
        }
        className="flex gap-0.5 overflow-x-auto [mask-image:linear-gradient(to_right,transparent,black_var(--rail-fade-left),black_calc(100%_-_var(--rail-fade-right)),transparent)] [scrollbar-width:none] md:flex-col md:gap-1 md:overflow-visible md:[mask-image:none]"
      >
        {decades.map((startYear) => {
          const id = garimposDecadeId(startYear);
          // One numbering system in the rail: full years ("1970"…"2020"); headings keep "Anos 70".
          const year = String(startYear);
          const isActive = id === activeId;
          return (
            <li key={startYear} className="flex-1 md:flex-none">
              <a
                href={`#${id}`}
                aria-current={isActive ? "location" : undefined}
                className={cn(
                  "inline-flex h-11 w-full items-center justify-center rounded-lg px-1 text-base sm:h-10 font-bold tabular-nums text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:justify-start md:px-3",
                  isActive && "bg-primary/10 text-primary hover:text-primary",
                )}
              >
                {year}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
